import { Select, Tag, Typography } from "antd";
import { keyBy } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { graphql } from "react-relay";
import { useLazyLoadQuery, useMutation, usePaginationFragment } from "react-relay/hooks";

import { Divider } from "@skydio/rivet-ui";

import { useSelector } from "hooks/redux";
import { canUserAuditOtherOrgs, getOrgUuid } from "state/current_user/selectors";
import { displayMutationResults } from "utils/mutations";

import { useConnectionNodes } from "../Pagination";
import { FALLBACK_COLOR } from "./iconStyles";

import type { SelectProps } from "antd";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";
import type { CSSProperties, JSX } from "react";
import type {
  CategorySelector_organization$data,
  CategorySelector_organization$key,
} from "__generated__/CategorySelector_organization.graphql";
import type { CategorySelector_query$key } from "__generated__/CategorySelector_query.graphql";
import type { CategorySelectorCreateCategoryMutation } from "__generated__/CategorySelectorCreateCategoryMutation.graphql";
import type { CategorySelectorInternalRefetchQuery } from "__generated__/CategorySelectorInternalRefetchQuery.graphql";
import type { CategorySelectorQuery } from "__generated__/CategorySelectorQuery.graphql";
import type { ConnectionNodeType } from "../Pagination";

export type Category = ConnectionNodeType<
  CategorySelector_organization$data["annotationCategories"]
>;

// No-op function for fallback Selector
// eslint-disable-next-line @typescript-eslint/no-empty-function
const NOOP = () => {};

type CategorySelectorSkeletonProps = SelectProps & {
  multiSelect: boolean;
};

export const CategorySelectorSkeleton = ({
  multiSelect,
  ...props
}: CategorySelectorSkeletonProps) => {
  const fallback = (
    <Select
      {...props}
      placeholder="Tags"
      mode={multiSelect ? "multiple" : undefined}
      onChange={NOOP}
    />
  );
  return fallback;
};

const categoryFragment = graphql`
  fragment CategorySelector_organization on Organization
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    searchTerm: { type: "String" }
  )
  @refetchable(queryName: "CategorySelectorRefetchQuery") {
    id
    annotationCategoryMeta {
      nextColor
    }
    annotationCategories(first: $count, after: $cursor, searchTerm: $searchTerm)
      @connection(key: "CategorySelector_annotationCategories") {
      __id
      edges {
        node {
          uuid
          id
          name
          color
          organizationId
        }
      }
    }
  }
`;

const internalCategoriesFragment = graphql`
  fragment CategorySelector_query on Query
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    searchTerm: { type: "String" }
  )
  @refetchable(queryName: "CategorySelectorInternalRefetchQuery") {
    annotationCategories(first: $count, after: $cursor, searchTerm: $searchTerm)
      @connection(key: "CategorySelectorGlobal_annotationCategories") {
      __id
      edges {
        node {
          uuid
          id
          name
          color
          organizationId
        }
      }
    }
  }
`;

const query = graphql`
  query CategorySelectorQuery(
    $orgUuid: String!
    $cursor: String
    $count: Int!
    $searchTerm: String
  ) {
    ...CategorySelector_query @arguments(count: $count, searchTerm: $searchTerm)
    organization(uuid: $orgUuid) {
      ...CategorySelector_organization
        @arguments(cursor: $cursor, count: $count, searchTerm: $searchTerm)
    }
  }
`;

const createCategoryMutation = graphql`
  mutation CategorySelectorCreateCategoryMutation(
    $name: String!
    $orgId: ID
    $connections: [ID!]!
  ) {
    createAnnotationCategory(name: $name, orgId: $orgId, generateColor: true) {
      annotationCategory
        @appendNode(connections: $connections, edgeTypeName: "AnnotationCategoryEdge") {
        uuid
        name
        id
        color
        organizationId
      }
      errors
    }
  }
`;

interface Props<T extends string | Array<string>, U extends Category | Category[]> {
  value?: T;
  onChange: (newVal: T) => void;
  onCategoryChange?: (newVal?: U) => void; // Optional callback for viewing the entire category on change
  onFocus?: () => void;
  onBlur?: () => void;
  multiSelect: boolean;
  allowCreate?: boolean;
  className?: string;
  style?: CSSProperties;
  valueField?: "id" | "uuid";
  allowClear?: boolean;
  displayOrgTags?: boolean;
  orgUuid?: string;
  autoFocus?: boolean;
  defaultOpen?: boolean;
}

const CategorySelector = <T extends string | Array<string>, U extends Category | Category[]>({
  value,
  onChange,
  onCategoryChange,
  allowCreate,
  className,
  multiSelect,
  onFocus,
  onBlur,
  style,
  valueField = "uuid",
  allowClear = true,
  displayOrgTags = true,
  orgUuid,
  autoFocus = false,
  defaultOpen = false,
}: Props<T, U>): JSX.Element => {
  const [searchTerm, setSearchTerm] = useState("");
  const currentOrgUuid = useSelector(getOrgUuid);
  orgUuid = orgUuid ?? currentOrgUuid;
  const isSkydioAdmin = useSelector(canUserAuditOtherOrgs);

  // TODO(rachel): Handle pagination when there are more than 500 categories.
  // https://skydio.atlassian.net/browse/SW-58287
  const data = useLazyLoadQuery<CategorySelectorQuery>(query, { orgUuid, count: 500 });

  const paginationQuery = usePaginationFragment<
    CategorySelectorQuery,
    CategorySelector_organization$key
  >(categoryFragment, data.organization);

  const internalCategoriesPaginationQuery = usePaginationFragment<
    CategorySelectorInternalRefetchQuery,
    CategorySelector_query$key
  >(internalCategoriesFragment, data);

  const [createCategory, loading] =
    useMutation<CategorySelectorCreateCategoryMutation>(createCategoryMutation);

  const orgId = paginationQuery.data?.id;
  const categories = useConnectionNodes(paginationQuery.data?.annotationCategories);
  const skydioAdminCategories = useConnectionNodes(
    internalCategoriesPaginationQuery.data?.annotationCategories
  );
  const categoryMap = useMemo(() => keyBy(categories, "uuid"), [categories]);

  const handleChange = useCallback(
    (newVal: T) => {
      onChange(newVal);
      if (Array.isArray(newVal)) {
        onCategoryChange?.(newVal.map(val => categoryMap[val]) as U);
      } else {
        onCategoryChange?.(categoryMap[newVal as string] as U);
      }
    },
    [onCategoryChange, onChange, categoryMap]
  );

  const addCategory = useCallback(
    (_orgId: string | null) => {
      createCategory({
        variables: {
          orgId: _orgId!,
          name: searchTerm,
          connections: [
            _orgId !== null
              ? paginationQuery.data?.annotationCategories?.__id || ""
              : internalCategoriesPaginationQuery.data?.annotationCategories?.__id || "",
          ],
        },
        onCompleted: displayMutationResults(response => response.createAnnotationCategory, {
          successMessage: c => `Created ${c.annotationCategory?.name}`,
          onSuccess: res => {
            paginationQuery.refetch({});
            const uuid = res.annotationCategory && res.annotationCategory[valueField];
            if (!uuid) return;
            if (multiSelect) {
              onChange([uuid] as T);
              onCategoryChange?.([res.annotationCategory] as U);
            } else {
              onChange(uuid as T);
              onCategoryChange?.(res.annotationCategory as U);
            }
          },
        }),
      });
    },
    [
      createCategory,
      searchTerm,
      paginationQuery,
      onChange,
      onCategoryChange,
      multiSelect,
      internalCategoriesPaginationQuery.data?.annotationCategories?.__id,
      valueField,
    ]
  );

  const options = useMemo(() => {
    const orgCategories =
      categories.map(category => (
        <Select.Option key={category.id} value={category[valueField]} title={category.name}>
          <Tag color={category.color || "orange"}>{category.name}</Tag>
        </Select.Option>
      )) || [];
    if (isSkydioAdmin) {
      return [
        displayOrgTags ? (
          <Select.OptGroup key={"org"} label={"Org Tags"}>
            {orgCategories}
          </Select.OptGroup>
        ) : null,
        <Select.OptGroup key={"skydio"} label={"Skydio Tags"}>
          {skydioAdminCategories.map(category => (
            <Select.Option key={category.id} value={category[valueField]} title={category.name}>
              <Tag color={category.color || "orange"}>{category.name}</Tag>
            </Select.Option>
          )) || []}
        </Select.OptGroup>,
      ];
    }
    return orgCategories;
  }, [categories, displayOrgTags, isSkydioAdmin, skydioAdminCategories, valueField]);

  const selectStyle = useMemo(() => ({ minWidth: 200, ...style }), [style]);
  const divStyle = useMemo(() => ({ padding: "0 8px 4px" }), []);
  const linkStyle = useMemo(() => ({ whiteSpace: "nowrap" as const }), []);
  const tagStyle = useMemo(() => ({ marginRight: 3 }), []);

  const onPreventMouseDown = useCallback((event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const tagRender = useCallback(
    (props: CustomTagProps) => {
      const { label, value, closable, onClose } = props;
      const color = categories.find(c => c[valueField] === value)?.color || "orange";
      return (
        <Tag
          color={color}
          onMouseDown={onPreventMouseDown}
          closable={closable}
          onClose={onClose}
          style={tagStyle}
        >
          {label}
        </Tag>
      );
    },
    [categories, valueField, onPreventMouseDown, tagStyle]
  );

  const showAddItem = useMemo(
    () =>
      !!searchTerm &&
      !categories.some(category => category.name === searchTerm) &&
      allowCreate !== false,
    [categories, searchTerm, allowCreate]
  );

  const handleAddOrgCategory = useCallback(() => {
    addCategory(orgId!);
  }, [addCategory, orgId]);

  const handleAddInternalCategory = useCallback(() => {
    addCategory(null);
  }, [addCategory]);

  const handleSelectClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const filterOption = useCallback((input: string, option: { title?: string } | undefined) => {
    return (option?.title as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0;
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const dropdownRender = useCallback(
    (menu: React.ReactNode) => (
      <>
        {menu}
        {showAddItem && (
          <>
            <Divider className="my-2" />
            <div
              className="flex flex-col items-start gap-2"
              style={divStyle}
              onMouseDown={handleMouseDown}
            >
              <Typography.Link disabled={loading} onClick={handleAddOrgCategory} style={linkStyle}>
                Create{" "}
                {
                  <Tag
                    color={
                      paginationQuery.data?.annotationCategoryMeta?.nextColor ?? FALLBACK_COLOR
                    }
                  >
                    {searchTerm}
                  </Tag>
                }
              </Typography.Link>
              {isSkydioAdmin && (
                <Typography.Link
                  disabled={loading}
                  onClick={handleAddInternalCategory}
                  style={linkStyle}
                >
                  Create internal {<Tag>{searchTerm}</Tag>}
                </Typography.Link>
              )}
            </div>
          </>
        )}
      </>
    ),
    [
      showAddItem,
      divStyle,
      loading,
      linkStyle,
      paginationQuery.data?.annotationCategoryMeta?.nextColor,
      searchTerm,
      isSkydioAdmin,
      handleAddOrgCategory,
      handleAddInternalCategory,
      handleMouseDown,
    ]
  );

  return (
    <Select
      style={selectStyle}
      showSearch
      mode={multiSelect ? "multiple" : undefined}
      className={className}
      placeholder="Tags"
      onFocus={onFocus}
      onBlur={onBlur}
      tagRender={tagRender}
      optionLabelProp="title"
      dropdownRender={dropdownRender}
      onSearch={setSearchTerm}
      searchValue={searchTerm}
      value={value}
      onChange={handleChange}
      filterOption={filterOption}
      allowClear={allowClear}
      autoFocus={autoFocus}
      defaultOpen={defaultOpen}
      onClick={handleSelectClick}
    >
      {options}
    </Select>
  );
};

export default CategorySelector;
